import React from 'react';
import AlertModal from '../modals/AlertModal';
import '../pages/SitePage.css';
import { BsCodeSlash, BsEnvelopeFill, BsFillImageFill, BsFillSendFill, BsFilm, BsGrid3X3GapFill } from 'react-icons/bs';
import MessageModal from '../modals/MessageModal';
import { Quill } from 'react-quill';
import { Server } from '../../server/server';
import SharedQuillEditor from './SharedQuillEditor';
import AddMediaModal from '../modals/AddMediaModal';
import AddEmbedModal from '../modals/AddEmbedModal';

let QuillBlock = Quill.import('blots/block');

interface PostPanelProps {
  placeholder?: string;
  maxLength?: number;
  images?: number;
  button?: string;
  onPost: Function;
  edit?: string;
  height?: string;
  enter?: boolean;
  minHeightOnFocus?: string;
}

interface PostPanelState {
  alert: string;
  message: string;
  openAddMedia: boolean;
  openEmbed: boolean;
}

class PostPanel extends React.Component<PostPanelProps, PostPanelState> {
  protected quillRef: any;
  protected quillPosition: number;
  protected quillWordCount: number;
  protected panelContainerRef: React.RefObject<HTMLDivElement>;
  protected sharedQuillEditorRef: React.RefObject<SharedQuillEditor>;

  constructor(props: PostPanelProps) {
    super(props);
    this.state = {
      alert: '',
      message: '',
      openAddMedia: false,
      openEmbed: false,
    };

    this.quillRef = null;
    this.quillPosition = 0;
    this.quillWordCount = 0;
    this.panelContainerRef = React.createRef<HTMLDivElement>();
    this.sharedQuillEditorRef = React.createRef<SharedQuillEditor>();
  }

  onQuillKeyDown = (event: any) => {
    if (this.props.enter && event.key === 'Enter') {
      event.preventDefault();
      if (this.props.edit) {
        this.onSave();
      } else {
        this.onPost();
      }
    }
  }

  onQuillFocus = () => {
    if (this.quillRef && this.quillRef.root && this.props.minHeightOnFocus) {
      (this.quillRef.root as HTMLElement).style.minHeight = this.props.minHeightOnFocus;
    }
  }

  onDocumentClick = (event: MouseEvent) => {
    if (this.quillRef && this.quillRef.root && this.props.minHeightOnFocus) {
      let okToResize = false;

      if (event.target && event.target instanceof HTMLElement && event.target.className == 'page-envelope') 
        okToResize = true;
      
      // Allow resize if the click occurred anywhere within the main panel (not just the exact panel element)
      // But do NOT allow if the target is a button or input element, even if it is inside the panel
      if (
        this.panelContainerRef.current &&
        this.panelContainerRef.current.contains(event.target as Node)
      ) {
        const targetElem = event.target as HTMLElement;
        if (
          targetElem.tagName !== 'BUTTON' &&
          targetElem.tagName !== 'INPUT' &&
          targetElem.tagName !== 'svg' &&
          targetElem.tagName !== 'path'
        ) {
          okToResize = true;
        }
      }

      if (okToResize) {
        const isFocused = document.activeElement === this.quillRef.root;
        if (!isFocused) {
          (this.quillRef.root as HTMLElement).style.minHeight = 'auto';
        }
      }
    }
  }

  setupQuillRef = (ref: any) => {
    this.quillRef = ref;
    if (this.quillRef && this.quillRef.root && this.props.minHeightOnFocus) {
      this.quillRef.root.addEventListener('focus', this.onQuillFocus);
    }
  }

  componentDidMount() {
    if (this.props.minHeightOnFocus) {
      document.addEventListener('click', this.onDocumentClick, true);
    }
  }

  componentWillUnmount() {
    if (this.quillRef && this.quillRef.root && this.props.minHeightOnFocus) {
      this.quillRef.root.removeEventListener('focus', this.onQuillFocus);
    }
    if (this.props.minHeightOnFocus) {
      document.removeEventListener('click', this.onDocumentClick, true);
    }
  }

  onMediaLibrary() {
    if(Server.isLoggedIn())
      this.setState({openAddMedia: true});
    else
      this.setState({alert: 'Please login to access your saved media files.'});
  }

  onInsertMedia(data: any) {
    this.setState({openAddMedia: false});
    this.quillRef.insertEmbed(this.quillPosition, data.category, data.url);
    this.quillRef.setSelection(this.quillPosition+1, 0);
  }

  onEmbedOpen() {
    if(Server.isLoggedIn())
      this.setState({openEmbed: true});
    else
      this.setState({alert: 'Please login to embed content from external websites.'});
  }

  async onEmbed(url:string) {
    this.setState({openEmbed: false});
    await this.sharedQuillEditorRef.current.embedUrl(url);
    setTimeout(() => {
      const length = this.quillRef.getLength();
      this.quillRef.setSelection(length, 0);
    }, 100);
  }

  onEmbedClose() {
    this.setState({openEmbed: false}); 
  }

  onAddImage() {
    if(!Server.isLoggedIn()) {
      this.setState({alert: 'Please login to add images from your device.'});
      return;
    }
    
    const fileElem = document.getElementById("PostPanelInput");
    if (fileElem)
      fileElem.click();
  }

  async onUploadMedia(file: any) {
    if (!file) return;

    if (file.type.indexOf('image') == -1) {
      this.setState({alert: 'Currently just image files are allowed.'});
      return;
    }

    this.setState({message: 'Saving image to your media library...'})
    let response = await Server.media.uploadFile(file);
    this.setState({message: ''})

    if (response.success) {
      let i = file.name.lastIndexOf('.');
      let fileExt = file.name.substring(i+1);
      let url = Server.media.getFileUrl({id: response.id, metadata: {ext: fileExt}});
      this.quillRef.insertEmbed(this.quillPosition, 'image', url);
      this.quillRef.setSelection(this.quillPosition+1, 0);
    }
  }

  validateContent(allowEmpty:boolean = false) {
    let content = this.quillRef.root.innerHTML;
    let contentText = this.quillRef.getText();
    let lines = this.quillRef.getLines();

    contentText = contentText.replace('\n', '');

    let blotCount = 0;
    for(let i = 0; i < lines.length; i++) {
      if(lines[i] instanceof QuillBlock)
        continue;
      blotCount++;
    }

    let imgBase64 = content.match(/src="data:image/gi);
    if (imgBase64 && imgBase64.length > 0) {
      this.setState({alert: 'No embedded images are allowed in posts.'});
      return null;
    }
  
    // if (blotCount > 1) {
    //   this.setState({alert: 'You can only embed one resource in an activity post.'});
    //   return null;
    // }
 
    if(blotCount == 0 && contentText.length == 0 && !allowEmpty) {
      console.warn('There is nothing to post.');
      this.setState({alert: 'There is nothing to post.'});
      return null;
    }

    if(this.props.maxLength && contentText.length > this.props.maxLength) {
      this.setState({alert: `Post must be at most ${this.props.maxLength} characters.`});
      return null;
    }

    if(content.substring(content.length-11) == '<p><br></p>') 
      content = content.substring(0, content.length-11);

    return encodeURIComponent(content);
  }

  extractUrls(html: string): string[] {
    let urls: string[] = [];
    let urlSet = new Set<string>();

    const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg|ico|tiff|tif|heic|heif)(\?|$)/i;

    const isImageUrl = (url: string): boolean => {
      return imageExtensions.test(url);
    };

    let parser = new DOMParser();
    let doc = parser.parseFromString(html, "text/html");

    // Extract text content only (excluding HTML attributes)
    const extractTextContent = (node: Node): string => {
      let text = '';
      if (node.nodeType === Node.TEXT_NODE) {
        text = node.textContent || '';
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Skip embedded content like ql-unfurl
        const element = node as Element;
        if (element.classList && element.classList.contains('ql-unfurl')) {
          return '';
        }
        
        // Process child nodes
        for (let child of Array.from(node.childNodes)) {
          text += extractTextContent(child);
        }
      }
      return text;
    };

    let textContent = extractTextContent(doc.body);

    // Extract URLs from text content only
    let urlRegex = /(f|ht){1}(tp|tps):\/\/([\w-]+\S)+[\w-]+([\w-?%#&=@.]*)?(\/[\w- ./?%#&=]*)?/g;
    let match;
    while ((match = urlRegex.exec(textContent)) !== null) {
      let url = match[0];
      if(!urlSet.has(url) && !isImageUrl(url)) {
        urls.push(url);
        urlSet.add(url);
      }
    }

    return urls;
  }

  async unfurlUrlsInEditor(urls: string[]) {
    if(!this.quillRef || !this.sharedQuillEditorRef.current) {
      return;
    }

    urls = urls.slice(0, 5);

    let length = this.quillRef.getLength();
    let position = length - 1;
    // this.quillRef.setSelection(position, 0);
    
    let textBefore = this.quillRef.getText(0, position);
    if(textBefore.length > 0 && !textBefore.endsWith('\n')) {
      this.quillRef.insertText(position, '\n');
      position = position + 1;
      // this.quillRef.setSelection(position, 0);
    }

    for(let url of urls) {
      this.quillRef.setSelection(position, 0);
      this.quillRef.insertText(position, '\n');
      let blankLinePosition = position;
      position = position + 1;
      // this.quillRef.setSelection(position, 0);
      
      let success = await this.sharedQuillEditorRef.current.embedUrl(url, false);
      
      if(!success) {
        this.quillRef.deleteText(blankLinePosition, 1);
        position = blankLinePosition;
      } else {
        length = this.quillRef.getLength();
        position = length - 1;
      }
      
      // this.quillRef.setSelection(position, 0);
    }
  }

  async onPost() {
    if(!Server.isLoggedIn()) {
      this.setState({alert: `Please login to ${this.props.button ? this.props.button.toLowerCase() : 'post'}.`});
      return;
    }

    if(!Server.network.isConnected()) {
      this.setState({alert: `You are not connected to the network.  Please refresh the browser to reconnect.`});
      return;
    }

    let content = this.validateContent();
    if(content == null)
      return;

    let decodedContent = decodeURIComponent(content);
    console.log(decodedContent);
    let urls = this.extractUrls(decodedContent);
    console.log(urls);
    
    if(urls.length > 0) {
      this.setState({message: 'Posting...'});
      await this.unfurlUrlsInEditor(urls);
      let updatedContent = this.quillRef.root.innerHTML;
      if(updatedContent.substring(updatedContent.length-11) == '<p><br></p>') 
        updatedContent = updatedContent.substring(0, updatedContent.length-11);
      content = encodeURIComponent(updatedContent);
      this.setState({message: ''});           
      this.props.onPost(content);
      this.quillRef.setText('');
      (this.quillRef.root as HTMLElement).style.minHeight = 'auto';
    }
    else {
      this.props.onPost(content);
      this.quillRef.setText('');
      (this.quillRef.root as HTMLElement).style.minHeight = 'auto';
    }
  }

  onCancel() {
    this.props.onPost(null);
  }

  onSave() {
    let content = this.validateContent(true);
    if(content == null)
      return;

    this.props.onPost(content);
  }

  renderPanel() {
    let height = this.props.height;
    if(!height)
      height = 'unset';

    return (
      <div className="site-page-panel" ref={this.panelContainerRef}>
        <SharedQuillEditor 
          ref={this.sharedQuillEditorRef}
          placeholder={this.props.placeholder ? this.props.placeholder : ' '}
          isActivity={true} 
          content={this.props.edit ? decodeURIComponent(this.props.edit) : null}
          onChange={(l:number)=>this.quillWordCount = l}
          getPosition={(p:number) => this.quillPosition = p}
          getRef={this.setupQuillRef} 
          height={height}
          onKeyDown={this.onQuillKeyDown}
        />

        <div className="site-page-row" style={{justifyContent: 'space-between'}}>
          <div className="site-page-row">
            <button className="site-page-icon-button" onClick={()=>this.onAddImage()}><BsFillImageFill /></button>
            <button className="site-page-icon-button" onClick={()=>this.onEmbedOpen()}><BsCodeSlash /></button>
            <button className="site-page-icon-button" onClick={()=>this.onMediaLibrary()}><BsGrid3X3GapFill /></button>
          </div>
          {!this.props.edit && 
            <button onClick={()=>this.onPost()}>
              {this.props.button ? this.props.button : 'Post'}
            </button>
          }
          {this.props.edit && 
            <div className="site-page-row" style={{justifyContent: 'right'}}>
              <button onClick={()=>this.onCancel()}>
                {this.props.button ? this.props.button : 'Cancel'}
              </button>
              <button onClick={()=>this.onSave()}>
                {this.props.button ? this.props.button : 'Save'}
              </button>
            </div>
          }
        </div>
      </div>
    )
  }

  render() {
    return (
      <div>

        {this.renderPanel()}

        <input
          type="file"
          id="PostPanelInput"
          accept="image/*, video/*, audio/*"
          style={{display: 'none'}}
          onChange={(e:any)=>this.onUploadMedia(e.currentTarget.files[0])}
        />

        <MessageModal message={this.state.message} />
        <AlertModal message={this.state.alert} button="OK" onClose={()=>this.setState({alert: ''})}/>

        <AddMediaModal 
          open={this.state.openAddMedia} 
          onClose={()=>this.setState({openAddMedia: false})} 
          callback={(data:any)=>this.onInsertMedia(data)} 
        />

        <AddEmbedModal 
          open={this.state.openEmbed} 
          onEmbed={(url:string)=>this.onEmbed(url)}
          onClose={()=>this.onEmbedClose()} 
          quillRef={this.quillRef}
          quillPosition={this.quillPosition}
        />
      </div>
    )
  }
}

export default PostPanel;